// Live order feed for the ticker bar.
//
// The ticker used to render six hardcoded orders in a fixed rotation, so every
// visitor saw the same materials, cities, yardages, and relative times on every
// load. This module replaces that with a weighted, persisted feed:
//
//   * WEIGHTED, not uniform-random — the mix leans toward what actually sells
//     (5/8" Minus and topsoil dominate; Seattle/Tacoma/Kent dominate), so it
//     reads like a real dispatch board rather than a shuffle.
//   * PERSISTED with absolute timestamps — the feed is written to
//     localStorage, so an order that says "1 hr ago" now says "2 hr ago" an
//     hour later instead of resetting on reload.
//   * SELF-AGING — entries older than MAX_AGE_MS are retired and fresh ones
//     are appended at plausible intervals, so the board keeps moving over a
//     long session or across repeat visits in the same day.

export type LiveFeedOrder = {
  material: string
  quantity: number
  city: string
  /** Epoch ms. Absolute so relative time keeps counting up across reloads. */
  createdAt: number
}

const STORAGE_KEY = '206gravel_live_feed_v1'
const FEED_SIZE = 6
/** Orders older than this drop off the board. */
const MAX_AGE_MS = 5 * 60 * 60 * 1000
/** Plausible spacing between consecutive orders. */
const MIN_GAP_MS = 11 * 60 * 1000
const MAX_GAP_MS = 47 * 60 * 1000

type Weighted<T> = { value: T; weight: number }

// Mix mirrors real order volume: base rock and topsoil carry the board, decor
// and specialty products appear occasionally.
const MATERIALS: Weighted<{ name: string; yards: number[] }>[] = [
  { value: { name: '5/8" Minus', yards: [12, 12, 12, 24, 8, 6] }, weight: 26 },
  { value: { name: '3-Way Topsoil', yards: [15, 15, 10, 30, 5] }, weight: 18 },
  { value: { name: '7/8" Drain Rock', yards: [12, 6, 4, 10] }, weight: 12 },
  { value: { name: 'Medium Bark', yards: [15, 15, 8, 30] }, weight: 11 },
  { value: { name: '1.25" Minus', yards: [12, 12, 24, 10] }, weight: 9 },
  { value: { name: 'Pea Gravel', yards: [6, 4, 12, 8] }, weight: 7 },
  { value: { name: 'Recycled Concrete', yards: [12, 24, 12] }, weight: 6 },
  { value: { name: '5/8" Crushed Clear', yards: [12, 8, 6] }, weight: 5 },
  { value: { name: 'Walkway Gravel', yards: [4, 6, 8] }, weight: 3 },
  { value: { name: '50/50 Mix', yards: [10, 15, 5] }, weight: 3 },
]

// Delivery volume by city — Seattle and the south-end corridor carry most of
// the runs, the Eastside and outlying cities appear less often.
const CITIES: Weighted<string>[] = [
  { value: 'Seattle', weight: 20 },
  { value: 'Tacoma', weight: 16 },
  { value: 'Kent', weight: 11 },
  { value: 'Renton', weight: 10 },
  { value: 'Auburn', weight: 8 },
  { value: 'Puyallup', weight: 8 },
  { value: 'Federal Way', weight: 7 },
  { value: 'Bellevue', weight: 6 },
  { value: 'Burien', weight: 5 },
  { value: 'Shoreline', weight: 4 },
  { value: 'Redmond', weight: 3 },
  { value: 'Lynnwood', weight: 2 },
]

function pickWeighted<T>(options: Weighted<T>[]): T {
  const total = options.reduce((sum, o) => sum + o.weight, 0)
  let roll = Math.random() * total
  for (const option of options) {
    roll -= option.weight
    if (roll <= 0) return option.value
  }
  return options[options.length - 1].value
}

function pick<T>(items: readonly T[]): T {
  return items[Math.floor(Math.random() * items.length)]
}

function randomGap(): number {
  return MIN_GAP_MS + Math.random() * (MAX_GAP_MS - MIN_GAP_MS)
}

function makeOrder(createdAt: number, avoidCity?: string): LiveFeedOrder {
  const material = pickWeighted(MATERIALS)
  let city = pickWeighted(CITIES)
  // Two identical cities back to back reads as a bug, so re-roll once.
  if (avoidCity && city === avoidCity) city = pickWeighted(CITIES)
  return {
    material: material.name,
    quantity: pick(material.yards),
    city,
    createdAt,
  }
}

function isValid(entry: unknown): entry is LiveFeedOrder {
  if (!entry || typeof entry !== 'object') return false
  const o = entry as LiveFeedOrder
  return (
    typeof o.material === 'string' &&
    typeof o.city === 'string' &&
    typeof o.quantity === 'number' &&
    typeof o.createdAt === 'number' &&
    Number.isFinite(o.createdAt)
  )
}

function read(): LiveFeedOrder[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter(isValid) : []
  } catch {
    return []
  }
}

function write(orders: LiveFeedOrder[]): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(orders))
  } catch {
    /* private window / quota — the in-memory feed still works */
  }
}

/**
 * Returns the current feed, newest first.
 *
 * Retires anything past MAX_AGE_MS, then tops the board back up to FEED_SIZE
 * with new orders spaced at plausible intervals. Because timestamps are
 * absolute and persisted, an entry keeps aging across reloads — "40 min ago"
 * becomes "2 hr ago" two hours later, then eventually drops off.
 */
export function getLiveFeed(now: number = Date.now()): LiveFeedOrder[] {
  const fresh = read()
    .filter((o) => now - o.createdAt < MAX_AGE_MS && o.createdAt <= now)
    .sort((a, b) => b.createdAt - a.createdAt)

  const orders = [...fresh]

  if (orders.length === 0) {
    // Cold start: build a full board walking backwards from a few minutes ago.
    let cursor = now - (3 * 60 * 1000 + Math.random() * 9 * 60 * 1000)
    for (let i = 0; i < FEED_SIZE; i++) {
      orders.push(makeOrder(Math.round(cursor), orders[i - 1]?.city))
      cursor -= randomGap()
    }
  } else {
    // Warm start: add any orders that "arrived" since the newest saved entry,
    // then backfill if aging retired more than we added.
    let cursor = orders[0].createdAt + randomGap()
    while (cursor <= now && orders.length < FEED_SIZE * 2) {
      orders.unshift(makeOrder(Math.round(cursor), orders[0]?.city))
      cursor += randomGap()
    }
    let oldest = orders[orders.length - 1].createdAt
    while (orders.length < FEED_SIZE) {
      oldest -= randomGap()
      if (now - oldest >= MAX_AGE_MS) break
      orders.push(makeOrder(Math.round(oldest), orders[orders.length - 1]?.city))
    }
  }

  const trimmed = orders.slice(0, FEED_SIZE)
  write(trimmed)
  return trimmed
}
